import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus, Crosshair, ArrowRight, Film, ListTree, Download } from "lucide-react";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth, isAdmin } from "../contexts/AuthContext";
import { PageEditor, ImportCourseDialog } from "../components/tiq/TiqEditors";
import { TIQ_SECTIONS } from "../components/tiq/sections";

function PageCard({ p, i, cfg }) {
  return (
    <Link to={`${cfg.base}/${p.slug}`} className="group card-surface relative border border-white/10 bg-[#141414] p-6 flex flex-col min-h-[220px] hover:border-[var(--org-accent)]/60" style={{ animation: `tiqUp .5s ${i * 0.07}s both cubic-bezier(.16,1,.3,1)`, transition: "border-color .2s" }} data-testid={`tiq-page-card-${p.slug}`}>
      <div className="text-[9px] uppercase tracking-[0.35em] text-[var(--org-accent)] font-bold">{p.eyebrow}</div>
      <h2 className="font-heading text-4xl uppercase tracking-tight text-white leading-none mt-2">{p.title}</h2>
      <p className="text-[13px] text-[#a0a0a0] leading-relaxed mt-3 flex-1 line-clamp-3">{p.tactic}</p>
      <div className="flex items-center gap-1.5 flex-wrap mt-4">
        {(p.categories || []).map(c => <span key={c.id} className="text-[9px] uppercase tracking-[0.2em] font-bold px-2 py-1" style={{ color: c.color, background: `${c.color}1a` }}>{c.name}</span>)}
      </div>
      <div className="flex items-center justify-between gap-3 mt-5 pt-4 border-t border-white/10 text-[10px] uppercase tracking-[0.25em] text-[#a0a0a0]">
        <span className="flex items-center gap-3"><span className="flex items-center gap-1"><ListTree className="w-3.5 h-3.5" /> {p.option_count} {cfg.counts?.[0] || "terms"}</span><span className="flex items-center gap-1"><Film className="w-3.5 h-3.5" /> {p.film_count} {cfg.counts?.[1] || "film"}</span></span>
        <span className="flex items-center gap-1 text-white group-hover:text-[var(--org-accent)]" style={{ transition: "color .15s" }}>Open <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5" style={{ transition: "transform .15s" }} /></span>
      </div>
    </Link>
  );
}

export default function TacticalIQList({ section = "tactical" }) {
  const cfg = TIQ_SECTIONS[section];
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [pages, setPages] = useState(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);

  const load = () => api.tiqPages(section).then(setPages).catch(() => setPages([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPages(null); load(); }, [section]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] pb-16" data-testid="tiq-list-page">
      <Navbar />
      <header className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(50% 80% at 10% 30%, rgba(58,160,255,.14), transparent 60%)" }} />
        <div className="relative max-w-[1200px] mx-auto px-5 lg:px-10 pt-12 sm:pt-16 pb-10 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-[var(--org-accent)] font-bold"><Crosshair className="w-3.5 h-3.5" /> {cfg.eyebrow}</div>
            <h1 className="font-heading text-6xl sm:text-7xl uppercase tracking-tight text-white leading-[0.9] mt-3" data-testid="tiq-section-title">{cfg.title}</h1>
            <p className="mt-4 max-w-2xl text-[15px] text-[#c0c0c0] leading-relaxed">{cfg.blurb}</p>
          </div>
          {admin && (
            <div className="flex items-center gap-2 flex-wrap">
            {!cfg.noImport && <button onClick={() => setImporting(true)} className="flex items-center gap-2 px-4 py-2.5 text-[10px] uppercase tracking-[0.3em] font-bold border border-white/20 text-white hover:border-[#3aa0ff] hover:text-[#3aa0ff]" style={{ transition: "color .15s, border-color .15s" }} data-testid="tiq-import-course-btn"><Download className="w-3.5 h-3.5" /> Import IQ course</button>}
            <button onClick={() => setCreating(true)} className="flex items-center gap-2 px-4 py-2.5 text-[10px] uppercase tracking-[0.3em] font-bold bg-[var(--org-accent)] text-[#121212] hover:bg-[var(--org-accent-hover)]" style={{ transition: "background-color .15s" }} data-testid="tiq-new-page-btn"><Plus className="w-3.5 h-3.5" /> New page</button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-5 lg:px-10 py-10">
        {pages === null ? (
          <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[var(--org-accent)]" /></div>
        ) : cfg.split ? (
          <div className="space-y-14" data-testid="tiq-page-grid">
            {cfg.split.map(sec => {
              const list = pages.filter(p => sec.match(p));
              return (
                <section key={sec.key} data-testid={`tiq-split-${sec.key}`}>
                  <div className="flex items-end justify-between gap-4 pb-4 mb-6 border-b border-white/10" style={{ borderLeft: `4px solid ${sec.color}`, paddingLeft: 16 }}>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.4em] font-bold" style={{ color: sec.color }}>{sec.eyebrow}</div>
                      <h2 className="font-heading text-4xl sm:text-5xl uppercase tracking-tight text-white leading-none mt-1" data-testid={`tiq-split-${sec.key}-title`}>{sec.title}</h2>
                      <p className="text-[13px] text-[#a0a0a0] mt-2 max-w-2xl">{sec.blurb}</p>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0] whitespace-nowrap" data-testid={`tiq-split-${sec.key}-count`}>{list.length} archetype{list.length === 1 ? "" : "s"}</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                    {list.map((p, i) => <PageCard key={p.id} p={p} i={i} cfg={cfg} />)}
                    {list.length === 0 && <div className="border border-dashed border-white/10 p-6 min-h-[120px] flex items-center justify-center text-[10px] uppercase tracking-[0.3em] text-[#575757]">No pages yet</div>}
                  </div>
                </section>
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="tiq-page-grid">
            {pages.map((p, i) => <PageCard key={p.id} p={p} i={i} cfg={cfg} />)}
            <div className="border border-dashed border-white/10 p-6 min-h-[220px] flex flex-col items-center justify-center text-center text-[#575757]" data-testid="tiq-more-soon">
              <Crosshair className="w-6 h-6 mb-2 opacity-50" />
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold">{pages.length === 0 ? "No pages yet" : "More coming"}</div>
              <div className="text-[12px] mt-1">{cfg.more}</div>
            </div>
          </div>
        )}
      </main>
      {creating && <PageEditor page={null} section={section} sectionTitle={cfg.title} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {importing && <ImportCourseDialog section={section} sectionTitle={cfg.title} onClose={() => setImporting(false)} onImported={() => { setImporting(false); load(); }} />}
      <style>{`@keyframes tiqUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}`}</style>
    </div>
  );
}
